import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Bell, Menu } from 'lucide-react';
import { useGorevSayaci } from '../../hooks/useGorevSayaci';
import http from '../../services/http';
import { mutlakMedyaUrl } from '../../utils/mediaUrl';
import { useAuth } from '../../hooks/useAuth';
import { useAuthContext } from '../../contexts/AuthContext';
import { ProfilePopover } from './ProfilePopover';
import { ChangePasswordModal } from './ChangePasswordModal';
import { logger } from '../../utils/logger';

interface HeaderProps {
  onToggleSidebar?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userName, userRole, userProfilUrl } = useAuth();
  const { logout, avatarVersion } = useAuthContext();
  // Sorumlusu olunan, son günü bugün ya da geçmiş açık görevler
  const gorevSayisi = useGorevSayaci();
  // Cache-bust render-time — profil_url DB/localStorage'da temiz tutulur.
  const avatarSrc = userProfilUrl
    ? `${mutlakMedyaUrl(userProfilUrl)}${avatarVersion ? `?v=${avatarVersion}` : ''}`
    : '';
  const [showAvatarPopover, setShowAvatarPopover] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const hasAvatar = !!userProfilUrl && !avatarError;

  // Route değişiminde popover + modal kapat (güvenlik: açık modal'da şifre alanları
  // başka sayfada yanlışlıkla görünmesin)
  useEffect(() => {
    setShowAvatarPopover(false);
    setShowChangePasswordModal(false);
  }, [location.pathname]);

  // Yeni avatar yüklenince (userProfilUrl değişince) önceki error state'ini sıfırla
  useEffect(() => {
    setAvatarError(false);
  }, [userProfilUrl]);

  const handleLogout = async () => {
    try {
      await http.post('/api/auth/logout');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        logger.error('Çıkış hatası:', error);
      }
    } finally {
      logout();
      navigate('/login');
    }
  };

  const getInitials = (name: string) => {
    const names = name.trim().split(' ');
    if (names.length === 0) return '';
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  const getPageTitle = () => {
    const path = location.pathname;
    // Exact match önce, sonra prefix match (uzundan kısaya sıralı — en spesifik eşleşsin)
    const routes: [string, string][] = [
      ['/', 'Ana Sayfa'],
      ['/orders/new', 'Yeni Sipariş'],
      ['/orders', 'Sipariş Yönetimi'],
      ['/customers/follow-ups', 'Müşteri Takipleri'],
      ['/customers/match-suggestions', 'Eşleştirme Önerileri'],
      ['/customers/groups', 'Müşteri Grupları'],
      ['/customers/new', 'Yeni Müşteri'],
      ['/customers', 'Müşteri Yönetimi'],
      // Görevler önce: başlık başla-eşleşmesiyle aranır, `/toplantilar` onu da yakalardı
      ['/toplantilar/gorevler', 'Görevler'],
      ['/toplantilar', 'Toplantılar'],
      ['/products/images', 'Görsel Yönetimi'],
      ['/products/catalog', 'Ürün Kataloğu'],
      ['/products/new', 'Yeni Ürün'],
      ['/products', 'Ürün Yönetimi'],
      ['/materials', 'Maliyet — Malzemeler'],
      ['/production', 'Üretim Ekranı'],
      ['/reports/sales-analysis/il-bazli-isi-haritasi', 'Raporlar — İl Isı Haritası'],
      ['/reports/payment-tracking', 'Raporlar — Ödeme Takip'],
      ['/reports/operational/teslimat-ciro', 'Raporlar — Teslimat & Ciro'],
      ['/reports/operational/termin-teslim-performansi', 'Raporlar — Termin & Teslim'],
      ['/reports/financial/financial-revenue-breakdown', 'Raporlar — Ciro Kırılımı'],
      ['/reports/product-analysis/product-performance', 'Raporlar — Ürün Performans'],
      ['/reports/teslim-tutanagi', 'Teslim Tutanağı'],
      ['/reports', 'Raporlar'],
      ['/users/new', 'Kullanıcı Davet Et'],
      ['/users', 'Kullanıcı Yönetimi'],
      ['/settings/integrations', 'Entegrasyon Ayarları'],
      ['/me/login-history', 'Giriş Geçmişim'],
      ['/kilavuz', 'Kullanım Kılavuzu'],
    ];
    if (path === '/') return 'Ana Sayfa';
    // Faz 4: /users/:id/activity dinamik segment — özel match
    if (/^\/users\/[^/]+\/activity$/.test(path)) return 'Kullanıcı Aktivitesi';
    for (const [route, title] of routes) {
      if (route !== '/' && path.startsWith(route)) return title;
    }
    return 'CRM Paneli';
  };

  return (
    <>
    <header className="h-14 bg-white border-b border-gray-200/80 flex items-center justify-between px-4 md:px-6 z-10">

      <div className="flex items-center gap-4">
        <button
          onClick={onToggleSidebar}
          className="text-gray-500 hover:text-gray-800 md:hidden focus:outline-none"
        >
          <Menu size={22} />
        </button>

        <div className="text-gray-800 font-semibold text-base md:text-lg tracking-tight">
          {getPageTitle()}
        </div>
      </div>

      <div className="flex items-center space-x-3 md:space-x-5">
        <button
          type="button"
          onClick={() => navigate('/toplantilar/gorevler')}
          className="text-gray-400 hover:text-primary transition-colors relative"
          title={gorevSayisi > 0 ? `Son günü gelmiş ${gorevSayisi} görevin var` : 'Görevler'}
          aria-label="Görevler"
        >
          <Bell size={19} />
          {gorevSayisi > 0 && (
            <span
              data-testid="gorev-sayaci"
              className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full ring-2 ring-white inline-flex items-center justify-center"
            >
              {gorevSayisi}
            </span>
          )}
        </button>

        <div className="h-5 w-px bg-gray-200 hidden md:block"></div>

        <div className="flex items-center space-x-3">
          <div className="text-right hidden md:block">
            <div className="text-sm font-semibold text-gray-700">{userName || 'Kullanıcı'}</div>
            <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{userRole || 'Misafir'}</div>
          </div>

          <div className="relative" ref={popoverRef}>
            <button
              type="button"
              onClick={() => setShowAvatarPopover(prev => !prev)}
              className={`h-8 w-8 md:h-9 md:w-9 rounded-full flex items-center justify-center text-sm border select-none overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/30 transition-shadow ${
                hasAvatar
                  ? 'border-primary/20'
                  : 'bg-primary/10 text-primary font-bold border-primary/20'
              }`}
            >
              {hasAvatar ? (
                <img src={avatarSrc} alt={userName} className="h-full w-full object-cover" onError={() => setAvatarError(true)} />
              ) : (
                getInitials(userName || 'K')
              )}
            </button>

            <ProfilePopover
              isOpen={showAvatarPopover}
              onClose={() => setShowAvatarPopover(false)}
              onChangePasswordClick={() => {
                setShowAvatarPopover(false);
                setShowChangePasswordModal(true);
              }}
              anchorRef={popoverRef}
            />
          </div>

          <button
            onClick={handleLogout}
            className="ml-1 text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50"
            title="Çıkış Yap"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>
    </header>

    <ChangePasswordModal
      isOpen={showChangePasswordModal}
      onClose={() => setShowChangePasswordModal(false)}
    />
    </>
  );
};

export default Header;
